import React, { FC, useEffect, useState } from 'react';
import firebase from 'firebase/app';
import moment from 'moment';
import prettyBytes from 'pretty-bytes';
import ArchyModal from 'components/shell/ArchyModal';
import { ui } from 'util/ui';
import useLabelNames from 'hooks/useLabelNames';
import {
  readMediaInfo,
  httpRange,
  MediaInfo,
  TrackInfo,
  describeResolution,
  describeRate,
  describeFrameRate,
  compressionNote,
} from 'util/mediaInfo';

/**
 * Admin and staff: a file's details (Aaron, 5 Nov 2026: "so people can see the compression quality of the
 * video"). The technical part comes from the file's own header, read with a few small range requests;
 * nothing is downloaded or changed.
 */

type Props = {
  video: IVideo;
  url: string;
  recordTitle?: string;
  onClose(): void;
};

type Row = [string, React.ReactNode];

const Section: FC<{ title: string; rows: Row[] }> = ({ title, rows }) => (
  <section style={{ marginBottom: 16 }}>
    <h3 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: ui.muted }}>{title}</h3>
    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(110px, 34%) 1fr', rowGap: 6, columnGap: 12, fontSize: 14 }}>
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt style={{ color: ui.muted }}>{k}</dt>
          <dd style={{ margin: 0, color: ui.ink, overflowWrap: 'anywhere' }}>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  </section>
);

const seconds = (s?: number) => {
  if (!s || !isFinite(s)) return '—';
  const d = moment.duration(s, 'seconds');
  const h = Math.floor(d.asHours());
  const two = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${h ? `${h}:` : ''}${h ? two(d.minutes()) : d.minutes()}:${two(d.seconds())} (${s.toFixed(2)} s)`;
};

const channels = (n?: number) => (!n ? '—' : n === 1 ? 'Mono' : n === 2 ? 'Stereo' : `${n} channels`);

const trackRows = (t: TrackInfo): Row[] => {
  if (t.kind === 'video') {
    const c = compressionNote(t);
    const rows: Row[] = [
      ['Codec', t.codecName],
      ['Resolution', describeResolution(t.width, t.height)],
      ['Frame rate', describeFrameRate(t.frameRate)],
      ['Bitrate', describeRate(t.bitrate)],
    ];
    if (t.bitDepth) rows.push(['Bit depth', `${t.bitDepth}-bit`]);
    if (c) {
      rows.push([
        'Compression',
        <>
          {c.note}
          {c.bpp !== undefined && (
            <span style={{ color: ui.muted }} title="Bits per pixel per frame: how much data each pixel gets. Higher means less compression.">
              {' '}
              ({c.bpp.toFixed(2)} bits per pixel)
            </span>
          )}
        </>,
      ]);
    }
    return rows;
  }
  const rows: Row[] = [
    ['Codec', t.codecName],
    ['Sample rate', t.sampleRate ? `${(t.sampleRate / 1000).toLocaleString('en-AU')} kHz` : '—'],
    ['Channels', channels(t.channels)],
  ];
  if (t.bitDepth) rows.push(['Bit depth', `${t.bitDepth}-bit`]);
  rows.push(['Bitrate', describeRate(t.bitrate)]);
  return rows;
};

/** What the browser itself can tell from a file it can play (used when the header isn't readable here). */
const browserProbe = (url: string, audioOnly: boolean): Promise<{ width?: number; height?: number; duration?: number } | null> =>
  new Promise((resolve) => {
    const el = document.createElement(audioOnly ? 'audio' : 'video') as HTMLVideoElement;
    const done = (v: { width?: number; height?: number; duration?: number } | null) => {
      el.removeAttribute('src');
      el.load();
      resolve(v);
    };
    const timer = setTimeout(() => done(null), 15000);
    el.preload = 'metadata';
    el.crossOrigin = 'anonymous';
    el.onloadedmetadata = () => {
      clearTimeout(timer);
      done({ width: el.videoWidth || undefined, height: el.videoHeight || undefined, duration: isFinite(el.duration) ? el.duration : undefined });
    };
    el.onerror = () => {
      clearTimeout(timer);
      done(null);
    };
    el.src = url;
  });

const FileInfo: FC<Props> = ({ video, url, recordTitle, onClose }) => {
  const names = useLabelNames() as Record<string, string>;
  const [stored, setStored] = useState<firebase.storage.FullMetadata | null>(null);
  const [media, setMedia] = useState<MediaInfo | null | undefined>(undefined); // undefined = still reading
  const [probe, setProbe] = useState<{ width?: number; height?: number; duration?: number } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [storedDone, setStoredDone] = useState(false);
  const [fileUrl, setFileUrl] = useState(url);
  const recordedSize = Number(video.size) || 0;
  // Some older records have no size; the stored file's own size is used then.
  const size = recordedSize || (stored ? stored.size : 0);
  const ext = (video.id.split('.').pop() || '').toLowerCase();

  useEffect(() => {
    let live = true;
    firebase
      .storage()
      .refFromURL(video.objectRef)
      .getMetadata()
      .then((m) => live && setStored(m))
      .catch(() => undefined)
      .finally(() => live && setStoredDone(true));
    return () => {
      live = false;
    };
  }, [video.objectRef]);

  // The row's own link may not have arrived (or failed): fetch it here then.
  useEffect(() => {
    if (url) return setFileUrl(url);
    let live = true;
    firebase
      .storage()
      .refFromURL(video.objectRef)
      .getDownloadURL()
      .then((u: string) => live && setFileUrl(u))
      .catch(() => {
        if (!live) return;
        setMedia(null);
        setProblem("The file couldn't be reached.");
      });
    return () => {
      live = false;
    };
  }, [url, video.objectRef]);

  const ready = !!fileUrl && (recordedSize > 0 || storedDone);
  useEffect(() => {
    if (!ready) return;
    let live = true;
    readMediaInfo(httpRange(fileUrl), size, video.id)
      .then(async (info) => {
        if (!live) return;
        setMedia(info);
        if (!info) {
          const p = await browserProbe(fileUrl, ['wav', 'mp3', 'aif', 'aiff', 'm4a'].includes(ext));
          if (live) setProbe(p);
        }
      })
      .catch((e) => {
        if (!live) return;
        setMedia(null);
        setProblem((e as Error).message);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const cold = video.category !== 'preview';
  const fileRows: Row[] = [
    ['Name', video.id],
    ['Size', size ? `${prettyBytes(size)} (${size.toLocaleString('en-AU')} bytes)` : '—'],
    ['Label', names[video.category] || video.category],
    ['Storage', cold ? 'Cold (Archive): a small retrieval fee per download' : 'Hot (Standard): plays here'],
    ['Uploaded', video.date ? moment.unix(video.date).format('D MMM YYYY, h:mm a') : '—'],
  ];
  if (recordTitle) fileRows.push(['Record', recordTitle]);
  if (video.notes) fileRows.push(['Notes', video.notes]);
  if (stored?.contentType) fileRows.push(['Content type', stored.contentType]);
  if (stored?.md5Hash) fileRows.push(['Checksum (MD5)', <code style={{ fontSize: 12 }}>{stored.md5Hash}</code>]);

  const video1 = media?.tracks.find((t) => t.kind === 'video');
  const audio = media ? media.tracks.filter((t) => t.kind === 'audio') : [];

  return (
    <ArchyModal title="File information" subtitle={video.id} onClose={onClose} width={560}>
      <div style={{ padding: '16px 20px 8px', fontFamily: ui.font }}>
        <Section title="File" rows={fileRows} />
        {media === undefined && <p style={{ margin: '0 0 16px', fontSize: 14, color: ui.muted }}>Reading the file's header…</p>}
        {media && (
          <Section
            title="Overall"
            rows={[
              ['Format', media.container],
              ['Length', seconds(media.duration)],
              ['Overall bitrate', describeRate(media.overallBitrate)],
            ]}
          />
        )}
        {video1 && <Section title="Video" rows={trackRows(video1)} />}
        {audio.map((t, i) => (
          <Section key={i} title={audio.length > 1 ? `Audio ${i + 1}` : 'Audio'} rows={trackRows(t)} />
        ))}
        {media === null && probe && (
          <Section
            title="From the browser"
            rows={[
              ...(probe.width ? ([['Resolution', describeResolution(probe.width, probe.height)]] as Row[]) : []),
              ['Length', seconds(probe.duration)],
              ['Average bitrate', probe.duration && size ? describeRate((size * 8) / probe.duration) : '—'],
            ]}
          />
        )}
        {media === null && !probe && (
          <p style={{ margin: '0 0 16px', fontSize: 13, color: ui.muted }}>
            {problem
              ? `Technical details aren't available for this file. ${problem}`
              : 'Technical details are read from MP4, MOV and WAV files. This file type shows its basic details only.'}
          </p>
        )}
      </div>
    </ArchyModal>
  );
};

export default FileInfo;
